// src/components/routes/Sign/Register/RegisterPassport.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Register.css';

const RegisterPassport = ({ formData: initialFormData }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState(initialFormData);
    const [passportFile, setPassportFile] = useState(null);

    const isPasswordValid = (password) => {
        const hasMinLength = password.length >= 8;
        const hasUpperCase = /([A-Z])/.test(password);
        const hasLowerCase = /([a-z])/.test(password);
        const hasNumber = /(\d)/.test(password);
        return hasMinLength && hasUpperCase && hasLowerCase && hasNumber;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert('File size must be less than 5MB');
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only JPG, JPEG, PNG and PDF files are allowed');
                return;
            }
            setPassportFile(file);
            alert('File uploaded successfully');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Form submitted');  // Debug log

        // Check for passport file first
        if (!passportFile) {
            alert('Please upload your passport document before proceeding with registration');
            return;
        }

        // Validate required fields
        const requiredFields = {
            username: 'Name',
            email: 'Email',
            telephone: 'Phone Number'
        };

        for (const [field, label] of Object.entries(requiredFields)) {
            if (!formData[field]) {
                alert(`Please enter ${label}`);

                return;
            }
        }

        if (!formData.password || formData.password !== formData.confirm_password) {
            alert('Please set and confirm your password');

            return;
        }

        if (!isPasswordValid(formData.password)) {
            alert('Password must be at least 8 characters long and contain uppercase, lowercase, and number');

            return;
        }

        try {
            setIsLoading(true);


            // Format phone number
            const formattedPhone = formData.telephone?.startsWith('+') 
                ? formData.telephone 
                : `+${formData.telephone}`;

            // Upload passport file with all required fields
            const uploadData = new FormData();
            uploadData.append('passport', passportFile);
            uploadData.append('email', formData.email);
            uploadData.append('password', formData.password);
            uploadData.append('username', formData.username);
            uploadData.append('telephone', formattedPhone);
            uploadData.append('card_select', 'Passport');
            uploadData.append('card_detail', '');  // Not required for passport registration
            uploadData.append('address', formData.address || '');
            uploadData.append('company_name', formData.company_name || '');
            uploadData.append('company_gst', formData.company_gst || '');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/registration/upload-passport`,
                uploadData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                alert('Passport uploaded successfully! You\'ll be verified shortly!');

                setFormData(prev => ({
                    ...prev,
                    passportPath: response.data.filePath,
                    isPassportUploaded: true
                }));
                setTimeout(() => navigate('/login'), 500);
            } else {
                throw new Error(response.data.message || 'Failed to upload passport');
            }

        } catch (error) {
            console.error('Registration Error:', error);
            alert(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="register-left">
            <h2 className="register-headi">Passport Verification</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-grouppp">
                        <label>Name*</label>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            readOnly
                            className="readonly-field"
                        />
                    </div>

                    <div className="form-grouppp">
                        <label>Email*</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            readOnly
                            className="readonly-field"
                        />
                    </div>

                    <div className="form-grouppp">
                        <label>Phone Number*</label>
                        <input
                            type="tel"
                            name="telephone"
                            value={formData.telephone}
                            readOnly
                            className="readonly-field"
                        />
                    </div>
                </div>

                <div className="form-grouppp">
                    <label>Upload Passport*</label>
                    <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileChange}
                        className="file-input"
                        required
                    />
                    <div className="file-requirements" style={{display: 'flex', flexDirection: 'column', color: '#EF6F53'}}>
                        <span style={{color: '#EF6F53'}}>Supported formats: JPG, JPEG, PNG, PDF (Max size: 5MB)</span>
                        {!passportFile && <span style={{marginTop: '5px', color: 'red', fontWeight: 'bold'}}>*Please upload your passport document</span>}
                    </div>
                </div>

                <div className="form-grouppp">
                    <label>Password*</label>
                    <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your password"
                    />
                    <div className="password-requirements">
                        <ul>
                            <li style={{ color: formData.password.length >= 8 ? 'green' : 'red' }}>
                                {formData.password.length >= 8 ? '✓' : '×'} At least 8 characters
                            </li>
                            <li style={{ color: /([A-Z])/.test(formData.password) ? 'green' : 'red' }}>
                                {/([A-Z])/.test(formData.password) ? '✓' : '×'} One uppercase letter
                            </li>
                            <li style={{ color: /([a-z])/.test(formData.password) ? 'green' : 'red' }}>
                                {/([a-z])/.test(formData.password) ? '✓' : '×'} One lowercase letter
                            </li>
                            <li style={{ color: /(\d)/.test(formData.password) ? 'green' : 'red' }}>
                                {/(\d)/.test(formData.password) ? '✓' : '×'} One number
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="form-grouppp">
                    <label>Confirm Password*</label>
                    <input
                        type="password"
                        name="confirm_password"
                        value={formData.confirm_password || ''}
                        onChange={handleInputChange}
                        required
                        placeholder="Confirm your password"
                    />
                </div>

                <button 
                    type="submit" 
                    disabled={isLoading || !formData.password || !formData.confirm_password || !isPasswordValid(formData.password) || formData.password !== formData.confirm_password}
                >
                    {isLoading ? 'Processing...' : 'Register'}
                </button>
            </form>

        </div>
    );
};

export default RegisterPassport;
